import React from 'react';

import { EMPTY_VALUE } from '../lib/formatters';
import { cn } from '../lib/utils';
import { PropertyRow } from './PropertyList';

interface FieldValueProps {
  label: string;
  /** The value. Absent (null / undefined / '') renders EMPTY_VALUE. */
  children?: React.ReactNode;
  /** Mono for machine-read values: slugs, image tags, ids. */
  mono?: boolean;
  /** A muted line under the value ("Permanent. …"). */
  help?: string;
  /**
   * `horizontal` (default): one row of the surrounding `PropertyList`, the same
   * columns as `Field orientation="horizontal"`. `vertical`: label over value,
   * the rhythm of a vertical Field, for a value standing among vertical inputs.
   */
  orientation?: 'horizontal' | 'vertical';
  className?: string;
}

/**
 * The read-only twin of `Field`: an input means you can change it, a
 * FieldValue means you cannot (DESIGN_SYSTEM.md §5 Record dialogs). Never a
 * disabled or input-styled control, never a bordered well. No copy button —
 * an id someone needs to copy is a `CredentialField`.
 */
export const FieldValue: React.FC<FieldValueProps> = ({
  label,
  children,
  mono = false,
  help,
  orientation = 'horizontal',
  className,
}) => {
  const absent = children === null || children === undefined || children === '';

  if (orientation === 'horizontal') {
    return (
      <>
        <PropertyRow label={label} mono={mono} className={className}>
          {children}
        </PropertyRow>
        {help ? <p className="col-start-2 text-xs text-muted-foreground">{help}</p> : null}
      </>
    );
  }

  return (
    <dl className={cn('space-y-1.5', className)} data-field-value="">
      <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
      <dd className={cn('min-w-0 break-words text-sm', mono && !absent && 'font-mono')}>
        {absent ? EMPTY_VALUE : children}
      </dd>
      {help ? <dd className="text-xs text-muted-foreground">{help}</dd> : null}
    </dl>
  );
};
